// Admin → Professions API client. The table read is the backend's mirror of
// the exact upstream fetch Trusstor's own Professions List page makes
// (org-wide `working?includeInactive=true`), so both dashboards show
// identical rows; `isActive` is resolved backend-side.

import type { IProfession, ITrade } from '@d-orca/shared/trusstor';
import { authedFetch, UnauthorizedError } from '@apis/auth';

/** A profession row with its activation state in the current site's project. */
export type IAdminProfession = IProfession & { isActive: boolean };

/** Backend answered 409 DUPLICATE_PROFESSION — same trade/specialty/contractor already exists. */
export class DuplicateProfessionError extends Error {
  constructor() {
    super('an identical profession already exists');
    this.name = 'DuplicateProfessionError';
  }
}

/** Deactivation refused — the profession still has tags or activities assigned. */
export class ProfessionInUseError extends Error {
  constructor(public readonly reason: 'tags' | 'activities') {
    super(`profession has ${reason} assigned`);
    this.name = 'ProfessionInUseError';
  }
}

export interface ICreateProfessionInput {
  tradeId: string;
  specialty?: string;
  contractor?: string;
  backgroundColor?: string;
}

async function parseErrors(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const error = body?.error as Record<string, unknown> | undefined;
  const code = error?.code as string | undefined;
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 409 && code === 'DUPLICATE_PROFESSION') throw new DuplicateProfessionError();
  if (res.status === 409 && code === 'PROFESSION_IN_USE') {
    const reason = (error?.details as { reason?: string } | undefined)?.reason;
    throw new ProfessionInUseError(reason === 'activities' ? 'activities' : 'tags');
  }
  throw new Error(`request failed: ${res.status} ${(error?.message as string | undefined) ?? ''}`);
}

export async function fetchAdminProfessions(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ professions: IAdminProfession[] }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/professions`, {
    signal,
  });
  if (res.ok) return (await res.json()) as { professions: IAdminProfession[] };
  return parseErrors(res);
}

export async function fetchAdminTrades(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ trades: ITrade[] }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/professions/trades`,
    { signal },
  );
  if (res.ok) return (await res.json()) as { trades: ITrade[] };
  return parseErrors(res);
}

export async function createProfession(
  siteId: string,
  input: ICreateProfessionInput,
): Promise<{ profession: IAdminProfession }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/professions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.ok) return (await res.json()) as { profession: IAdminProfession };
  return parseErrors(res);
}

async function patchProfession(
  siteId: string,
  professionId: string,
  body: { backgroundColor: string } | { active: boolean },
): Promise<{ profession: IAdminProfession }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/professions/${encodeURIComponent(professionId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (res.ok) return (await res.json()) as { profession: IAdminProfession };
  return parseErrors(res);
}

export function updateProfessionColor(
  siteId: string,
  professionId: string,
  backgroundColor: string,
): Promise<{ profession: IAdminProfession }> {
  return patchProfession(siteId, professionId, { backgroundColor });
}

export function setProfessionActive(
  siteId: string,
  professionId: string,
  active: boolean,
): Promise<{ profession: IAdminProfession }> {
  return patchProfession(siteId, professionId, { active });
}

export async function deleteProfession(
  siteId: string,
  professionId: string,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/professions/${encodeURIComponent(professionId)}`,
    { method: 'DELETE' },
  );
  if (res.ok) return;
  return parseErrors(res);
}
